'use client';

import * as React from 'react';

import { Calendar24Regular } from '@fluentui/react-icons';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { cn, isDateValid } from '@avodah-inc/ui/utils';

import { UnstyledButton } from '../../atoms';
import { Popover, PopoverContent, PopoverTrigger } from '../../atoms/popover';
import type { TextFieldProps } from '../../atoms/text-field';
import { TextField } from '../../atoms/text-field';
import { Calendar } from '../calendar';

dayjs.extend(localizedFormat);

export type DatePickerProps = Omit<
  TextFieldProps,
  'onSelect' | 'defaultValue'
> & {
  onSelect?: (date: Date | undefined) => void;
  defaultValue?: string;
  disablePast?: boolean;
  fluid?: boolean;
  dataTestId?: string;
};

export const DatePicker = ({
  defaultValue,
  onSelect,
  disablePast,
  placeholder = 'Pick a date',
  ...props
}: DatePickerProps) => {
  const [date, setDate] = React.useState<Date>(
    defaultValue ? new Date(defaultValue) : new Date(),
  );

  const [showCalendar, setShowCalendar] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>(() =>
    defaultValue ? dayjs(defaultValue).format('L') : '',
  );
  const inputRef = React.useRef<HTMLInputElement>(null);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  const handleInputChange = (input: string) => {
    const cleanedInput = input.replace(/[^\d]/g, '');

    let formattedInput = cleanedInput.slice(0, 2);
    if (cleanedInput.length > 2) {
      formattedInput += `/${cleanedInput.slice(2, 4)}`;
    }
    if (cleanedInput.length > 4) {
      formattedInput += `/${cleanedInput.slice(4, 8)}`;
    }

    setInputValue(formattedInput);

    const matches =
      /^(?<group1>\d{2})\/(?<group2>\d{2})\/(?<group3>\d{4})$/.exec(
        formattedInput,
      );

    if (!matches) {
      return;
    }

    const [, month, day, year] = matches;
    const dateValue = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD').toDate();

    if (isDateValid({ date: dateValue })) {
      setDate(dateValue);
      onSelect?.(dateValue);
    }
  };

  const handleInputBlur = () => {
    setInputValue(dayjs(date).format('L'));
  };

  const handleSelect = React.useCallback((selectedDate: Date | undefined) => {
    if (selectedDate !== undefined) {
      setDate(selectedDate);
      setInputValue(dayjs(selectedDate).format('L'));
    }
    setShowCalendar(false);
    onSelect?.(selectedDate);
  }, []);

  const addFromDate = disablePast ? { fromDate: new Date() } : {};

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverContentRef.current &&
        !popoverContentRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          inputRef.current?.focus();
        }
      }}
      open={showCalendar}
    >
      <PopoverTrigger className={cn({ 'w-full': props.fluid })}>
        <TextField
          isRequired={props.isRequired}
          label="Date"
          rightIcon={
            <UnstyledButton
              onPress={() => {
                setShowCalendar((e) => !e);
              }}
            >
              <Calendar24Regular
                className={cn('text-primary', {
                  'text-gray-300': props.isDisabled,
                })}
              />
            </UnstyledButton>
          }
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onClick={() => {
            setShowCalendar(true);
          }}
          onFocus={() => {
            setShowCalendar(true);
          }}
          placeholder={placeholder}
          ref={inputRef}
          value={inputValue}
          dataTestId={props.dataTestId}
          {...props}
        />
      </PopoverTrigger>

      <PopoverContent className="w-100" ref={popoverContentRef}>
        <Calendar
          initialFocus={false}
          mode="single"
          {...addFromDate}
          selected={date}
          onDayClick={(selectDate) => {
            handleSelect(selectDate);
          }}
          onTodayClick={() => {
            const selectedDate = new Date();
            handleSelect(selectedDate);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
